import { useState } from "react";
import type { ReactNode } from "react";
import { BottomCTA, BottomSheet, IconArrowClockwiseCircularLine, IconBoltFill, IconChatLine, IconLockFill, IconSparkle2Fill, ModelCard } from "@hamlak/ui";
import type { ModelId, ModelInfo } from "@/api/types";
import { t } from "@/i18n/ko";

type Props = {
  open: boolean;
  models: readonly ModelInfo[];
  current: ModelId | undefined;
  onApply: (model: ModelId) => void;
  onClose: () => void;
  /** 설정의 기본 대화 모델(1.18)은 제목을 넘긴다 → 설명 없이 "변경"·"닫기". 없으면 채팅방 시안(F04-07) 구성. */
  title?: string;
  /** 채팅방만: 헤더 보유 브릭 pill. */
  balance?: number | null | undefined;
  applying?: boolean;
};

type IconKey = "chat" | "sparkle" | "arrow" | "bolt" | "lock";
const ICONS: Record<IconKey, () => ReactNode> = {
  chat: () => <IconChatLine />,
  sparkle: () => <IconSparkle2Fill />,
  arrow: () => <IconArrowClockwiseCircularLine />,
  bolt: () => <IconBoltFill />,
  lock: () => <IconLockFill />,
};
/** 아는 모델 id → 시안 아이콘(483:4424: 노크=ChatLine, 돌파=Sparkle2Fill) */
const KNOWN: Partial<Record<ModelId, IconKey>> = { knock: "chat", break: "sparkle" };
/** 모르는 id(어드민이 추가한 모델)는 단가 낮은 순으로 남은 아이콘을 차례로 — 시안 5개(똑똑=ArrowClockwise · 와르르=Bolt · 함락=Lock)와 같은 순서, 겹치지 않게 */
const FALLBACK: readonly IconKey[] = ["arrow", "bolt", "lock", "chat", "sparkle"];

/** 정렬된 모델 목록 → id 별 아이콘. 풀을 다 쓰면 마지막 규칙(isDefault 면 ChatLine, 아니면 Sparkle) */
export function modelIcons(sorted: readonly ModelInfo[]): Map<ModelId, ReactNode> {
  const used = new Set<IconKey>(sorted.flatMap((m) => (KNOWN[m.id] ? [KNOWN[m.id]!] : [])));
  const pool = FALLBACK.filter((k) => !used.has(k));
  return new Map(
    sorted.map((m) => {
      const key = KNOWN[m.id] ?? pool.shift() ?? (m.isDefault ? "chat" : "sparkle");
      return [m.id, ICONS[key]()];
    }),
  );
}

/** 1.10 ModelSheet(F04-07): "대화 모델" + 설명 + 보유 → ModelCard × pricing.models(role=radio, 시안대로 단가 낮은 순) → "이 모델로 계속하기". 문구·단가는 서버 값. */
export function ModelSheet({ open, models, current, onApply, onClose, title, balance, applying = false }: Props) {
  // 시트 안의 임시 선택. 닫거나 적용하면 버리고 다음에 열 때 current에서 다시 시작한다.
  const [picked, setPicked] = useState<ModelId | null>(null);
  const selected = picked ?? current;
  const inChat = title === undefined;
  const sorted = [...models].sort((a, b) => a.costPerTurn - b.costPerTurn);
  const icons = modelIcons(sorted);
  const close = () => {
    setPicked(null);
    onClose();
  };
  return (
    <BottomSheet
      open={open}
      onClose={close}
      header={{
        title: title ?? t("chat.model.title"),
        ...(inChat ? { description: t("chat.model.description") } : {}),
        ...(inChat && balance != null ? { brickCount: balance } : {}),
      }}
      data-testid="model-sheet"
      footer={
        <BottomCTA
          fixed={false}
          primary={{
            children: inChat ? t("chat.model.continue") : t("chat.model.apply"),
            disabled: !selected || applying,
            loading: applying,
            onClick: () => {
              if (!selected) return;
              setPicked(null);
              onApply(selected);
            },
          }}
          {...(inChat ? {} : { secondary: { children: t("common.close"), onClick: close } })}
        />
      }
    >
      {/* 목록만 스크롤, CTA 는 시트 footer(스크롤 밖) */}
      <div className="chat__sheetBody">
        <div role="radiogroup" aria-label={title ?? t("chat.model.title")} className="chat__modelList">
          {sorted.map((m) => (
            <ModelCard
              key={m.id}
              name={m.name}
              summary={m.description}
              cost={m.costPerTurn}
              icon={icons.get(m.id)}
              {...(m.isDefault ? { tag: t("chat.model.default") } : {})}
              selected={selected === m.id}
              onClick={() => setPicked(m.id)}
            />
          ))}
        </div>
      </div>
    </BottomSheet>
  );
}
